import { inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { UserService } from 'src/app/shared/services/user.service';
import { Role, User, UserSearchEvent } from '../user.model';
import { PagedResult } from 'src/app/shared/models/paging';
import { Organization } from '../../organization/organization-models';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseListStateService, IFilterControls } from 'src/app/shared/models/base.state.service';

interface IUsersFilterValues {
  query: string | null;
  organizationId: number | null;
  includeDisabled: boolean;
  roleName: string | null;
}

export enum UsersListQueryParams {
  query = 'query',
  organizationId = 'organizationId',
  includeDisabled = 'includeDisabled',
  roleName = 'roleName',
}
export class UserListStateService extends BaseListStateService<User, UsersListQueryParams, IUsersFilterValues> {
  constructor() {
    super();
    this.resetPageOnFilterChange();
  }

  updateRole(role: null) {
    this.ToFirstPage({ roleName: role });
  }
  public userRoles: Role[] = [];
  public resetOrgTrigger = signal<{ reset: boolean }>({ reset: false });
  private userService = inject(UserService);
  fetchedOrganizations = signal<Organization[]>([]);

  readonly form: FormGroup<IFilterControls<IUsersFilterValues>> = new FormGroup<IFilterControls<IUsersFilterValues>>({
    query: new FormControl<string | null>(
      this.queryParamMap$()?.get(UsersListQueryParams.query)
        ? decodeURIComponent(this.queryParamMap$()?.get(UsersListQueryParams.query) ?? '')
        : null
    ),
    roleName: new FormControl<string | null>(
      this.queryParamMap$()?.get(UsersListQueryParams.roleName)
        ? decodeURIComponent(this.queryParamMap$()?.get(UsersListQueryParams.roleName) ?? '')
        : null
    ),
    includeDisabled: new FormControl<boolean>(
      this.queryParamMap$()?.get(UsersListQueryParams.includeDisabled)
        ? decodeURIComponent(this.queryParamMap$()?.get(UsersListQueryParams.includeDisabled) ?? '') === 'true'
        : false,
      { nonNullable: true }
    ),
    organizationId: new FormControl<number | null>(
      this.queryParamMap$()?.get(UsersListQueryParams.organizationId)
        ? parseInt(this.queryParamMap$()?.get(UsersListQueryParams.organizationId) ?? '0')
        : null
    ),
  });

  protected createQueryParams(filters: Partial<IUsersFilterValues>): Record<string, string | null> {
    const queryParams: Record<UsersListQueryParams, string | null> = {
      [UsersListQueryParams.includeDisabled]: filters.includeDisabled ? filters.includeDisabled.toString() : null,
      [UsersListQueryParams.organizationId]: filters.organizationId ? filters.organizationId.toString() : null,
      [UsersListQueryParams.roleName]: filters.roleName ? encodeURIComponent(filters.roleName) : null,
      [UsersListQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
    };
    return queryParams;
  }

  users$: Observable<PagedResult<User>> = combineLatest([
    toObservable(this.refreshTrigger),
    this.form.valueChanges.pipe(startWith(this.form.value)),
    toObservable(this.viewState),
    this.userService.getRoles(),
  ]).pipe(
    takeUntilDestroyed(),
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this.loading.set(true)),
    tap(([_, form]) => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: this.createQueryParams(form),
        queryParamsHandling: 'merge',
      });
    }),
    switchMap(([refresh, filters, viewState, userRoles]) => {
      const event = <UserSearchEvent>{
        query: filters.query,
        sorting: viewState.sort,
        includeDisabled: filters.includeDisabled,
        paging: viewState.page,
        roleName: filters.roleName,
        organizationId: filters.organizationId,
      };
      this.userRoles = userRoles;
      return this.userService.getUsers(event);
    }),
    catchError(() => of(<PagedResult<User>>{ items: [], totalCount: 0, pageIndex: 0, pageSize: this.defaultPageSize })),
    tap(users => {
      this.enrichRoles(users.items);
      this.loading.set(false);
    })
  );

  triggerRefresh() {
    this.refreshTrigger.update(x => x + 1);
  }

  private enrichRoles(users: User[]) {
    users.forEach(user => {
      const userRoles: Role[] = [];
      user.rolesAsStrings.forEach(userRole => {
        if (userRole === 'sales') {
          return;
        }
        const foundRole = this.userRoles.find(r => r.name === userRole);
        if (foundRole) {
          userRoles.push(foundRole);
        }
      });
      user.roles = userRoles;
    });
  }

  public resetFilter() {
    this.form.patchValue({
      query: null,
      organizationId: null,
      includeDisabled: false,
      roleName: null,
    });
    this.viewState.update(current => ({
      ...current,
      sort: this.defaultSort,
      page: { length: 0, pageIndex: 0, pageSize: this.defaultPageSize },
    }));
    this.resetOrgTrigger.set({ reset: true });
    this.fetchedOrganizations.set([]);
  }
}
